import { twMerge } from '@shared/themes';
import Image from 'next/image';
import { GraphicContainer } from '../GraphicContainer/GraphicContainer';
import type { ContentType } from '../types';
import { Winning } from '../Winning/Winning';
import { PromotionPodFooter, PromotionPodGraphicContainer, PromotionPodHeader } from './components/PromotionPodComponents';
import { PromotionProgressBar } from './components/PromotionProgressBar/PromotionProgressBar';
import type { PromotionPodVariant } from './types/types';

type PromotionPodViewProps = {
  data: Partial<ContentType>;
  variant?: PromotionPodVariant;
  grayscale?: boolean;
  ctaCallback?: () => void;
  moreInfoCtaCallback?: () => void;
};

/**
 * Presentational (server-friendly) UI of the promotion pod: graphic, header, optional progress
 * bar / winnings, and footer. It holds no client state — notification-reactive chrome (border,
 * halo, "new" badge, body z-index) is handled by {@link PromotionPodClient} and its leaves.
 *
 * Rendered as the child of {@link PromotionPodClient}, so this whole tree stays server rendered.
 */
function PromotionPodView({ data, variant = 'offers', grayscale, ctaCallback, moreInfoCtaCallback }: PromotionPodViewProps) {
  const {
    title,
    description,
    legalText,
    expirationDate,
    status,
    bundleCode,
    iterationNr,
    image,
    progress,
    moreInfoCta,
    cta,
    ctaHref,
    bonusBalanceText,
    bonusProgressText,
    gameLogo,
    moreInfoCtaModal,
    wins,
    callout,
  } = data;

  const shouldApplyGrayscale = grayscale || status === 'expired';
  const shouldShowFreeSpinsProgressBar = !(['completed', 'expired'].includes(status ?? '') && variant === 'free-spins');
  const shouldShowProgressBar = progress && bonusProgressText && status !== 'pending' && shouldShowFreeSpinsProgressBar;
  const showBalance = variant === 'bonuses';
  const shouldShowWinningsOnFreeSpinsPod = variant === 'free-spins' && wins && wins.fixed_amount > 0;

  return (
    <>
      <PromotionPodGraphicContainer data={{ image }} grayscale={shouldApplyGrayscale}>
        {gameLogo && (
          <Image
            src={gameLogo}
            alt="Game logo"
            width={100}
            height={100}
            className={twMerge('absolute inset-0 size-full object-contain', shouldApplyGrayscale ? 'grayscale' : '')}
          />
        )}
        <GraphicContainer className="bg-transparent" />
      </PromotionPodGraphicContainer>
      <div className="p-4 flex flex-col gap-4 justify-between items-start flex-1 z-2">
        <PromotionPodHeader data={{ expirationDate, title, description, status, callout }} variant={variant} />
        {variant !== 'offers' && shouldShowProgressBar ? (
          <PromotionProgressBar {...progress} balanceText={bonusBalanceText} progressText={bonusProgressText} showBalance={showBalance} />
        ) : null}
        {shouldShowWinningsOnFreeSpinsPod ? <Winning winnings={wins} label="winnings.offer" namespace="offers.page.obs.offer" className="w-full" /> : null}
        <PromotionPodFooter
          data={{ moreInfoCta, legalText, status, bundleCode, iterationNr, progress, cta, ctaHref, moreInfoCtaModal }}
          ctaCallback={ctaCallback}
          moreInfoCtaCallback={moreInfoCtaCallback}
        />
      </div>
    </>
  );
}

export { PromotionPodView };
export type { PromotionPodViewProps };
